'use client';

import { Textarea } from '@components/inputs';
import { Dialog, Transition } from '@headlessui/react';
import { Button } from '@ngg/components';
import { XIcon } from '@ngg/icons';
import Image from 'next/image';
import type { FormEvent } from 'react';
import { Fragment, useRef, useState } from 'react';
import type { Dictionary } from 'types/common';
import type { Product, Variant } from 'types/product';

import { cn } from '@/lib/utils';

export const Star = ({
    className,
    filled,
}: {
    className: string;
    filled?: boolean;
}) => (
    <svg
        width="1.22em"
        height="1.22em"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(className, filled && 'fill-black')}>
        <path
            d="M12 4.5L13.9838 9.26957L19.1329 9.68237L15.2098 13.0429L16.4084 18.0676L12 15.375L7.59161 18.0676L8.79018 13.0429L4.86708 9.68237L10.0162 9.26957L12 4.5Z"
            strokeLinecap="square"
            strokeWidth="1"
            stroke="currentColor"
        />
    </svg>
);

export const ReviewProductForm = ({
    onSubmit,
    score = 0,
    comment,
    isLoading,
    classNames,
    onClose,
    dictionary,
}: {
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    score?: number;
    comment?: string;
    classNames?: {
        wrapper?: string;
    };
    onClose?: () => void;
    isLoading?: boolean;
    dictionary: Dictionary;
}) => {
    const [stars, setStars] = useState(score);
    const formRef = useRef<HTMLFormElement | null>(null);
    const [newComment, setNewComment] = useState(comment);

    const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
        if (onSubmit) {
            onSubmit(e);
            formRef?.current?.reset();
        }
    };

    return (
        <form
            ref={formRef}
            action=""
            onSubmit={onSubmitForm}
            className={cn(
                'relative col-span-6 flex min-h-full flex-col px-4 pb-6 md:p-6',
                classNames?.wrapper,
            )}>
            <div className="flex items-center">
                <h3 className="hidden text-lg font-bold md:block">
                    {dictionary.leaveReview}
                </h3>
                {onClose ? (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-3 text-base">
                        <XIcon />
                    </button>
                ) : null}
            </div>
            <div className="group mt-1 flex w-fit">
                {[...Array(5)].map((_, i) => (
                    <button
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        type="button"
                        className="peer relative h-6 w-6 text-xl peer-hover:[&>:last-child]:opacity-0 peer-hover:[&>:last-child]:transition"
                        onClick={() => setStars(i + 1)}>
                        <Star
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:opacity-0 group-hover:transition"
                            filled={i < stars}
                        />
                        <Star className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0  transition group-hover:opacity-100" />
                        <Star className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 fill-black opacity-0 group-hover:opacity-100" />
                    </button>
                ))}
            </div>

            <input type="number" name="score" value={stars} hidden readOnly />

            <label
                htmlFor="leaveReview"
                className="mt-6 hidden text-base md:block">
                {dictionary.leaveAReviewLabel}
            </label>
            <Textarea
                id="review"
                name="comment"
                value={newComment}
                onChange={(e) => setNewComment(e.currentTarget.value)}
                placeholder={dictionary.leaveReviewPlaceholder}
                resizable="none"
                fullWidth
                classNames={{
                    wrapper: 'flex-grow child:h-full mt-4 md:mt-2',
                    textarea: 'placeholder:text-grey-300 p-2 h-full',
                }}
            />
            <Button
                type="submit"
                isLoading={isLoading}
                className="btn-primary mt-2 md:mt-4"
                fullWidth>
                {dictionary.leaveReview}
            </Button>
            {dictionary.reviewDescription ? (
                <p className="mt-4 text-xxs text-grey-300">
                    {dictionary.reviewDescription}
                </p>
            ) : null}
        </form>
    );
};

export default function ReviewProductDialog({
    buttonText,
    product,
    isEdit = false,
    className,
    onSubmit,
    onDelete,
    score = 0,
    comment,
    isLoading,
    dictionary,
}: {
    buttonText: string;
    product: Product | Variant;
    isEdit?: boolean;
    score?: number;
    /** className for the button */
    className?: string;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => Promise<boolean>;
    onDelete?: () => Promise<boolean>;
    comment?: string;
    isLoading?: boolean;
    dictionary: Dictionary;
}) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(true);
                }}
                className={className}>
                {buttonText}
            </button>
            <Transition show={isOpen} as={Fragment}>
                <Dialog
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    className="relative z-50">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <div
                            className="fixed inset-0 bg-black/20"
                            aria-hidden="true"
                        />
                    </Transition.Child>

                    {/* Full-screen container to center the panel */}
                    <Transition.Child
                        className="fixed inset-0 flex w-screen items-center justify-center p-6"
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95">
                        <Dialog.Panel className="relative mx-auto grid w-full max-w-2xl grid-cols-2 bg-white md:grid-cols-10">
                            <button
                                className="absolute right-4 top-4 z-10 text-base underline md:hidden"
                                type="button"
                                onClick={() => setIsOpen(false)}>
                                <XIcon />
                            </button>

                            <div className="col-span-4 border-grey-150 md:border-r">
                                <div className="relative aspect-video overflow-hidden bg-grey-100 md:aspect-portrait">
                                    {product?.images?.[0].src && (
                                        <Image
                                            src={product.images[0].src}
                                            fill
                                            alt=""
                                            className="object-contain p-4 mix-blend-multiply"
                                        />
                                    )}
                                </div>
                                <div className="relative px-4 pt-4 md:static md:pb-6 md:pt-4.5">
                                    <Dialog.Title className="mb-5 text-lg font-bold md:hidden">
                                        {dictionary.leaveReview}
                                    </Dialog.Title>
                                    {isEdit && onDelete ? (
                                        <button
                                            type="button"
                                            className="hover:cursor absolute right-4 top-2 z-10 text-xs text-grey-300 underline hover:no-underline md:right-6 md:top-4"
                                            onClick={async () => {
                                                if (onDelete) {
                                                    const isSuccess =
                                                        await onDelete();
                                                    if (isSuccess)
                                                        setIsOpen(false);
                                                }
                                            }}>
                                            {dictionary.removeReview}
                                        </button>
                                    ) : null}
                                    <p className="text-xxs uppercase">
                                        {product.brand}
                                    </p>
                                    <p className="mt-2">{product.title}</p>
                                </div>
                            </div>

                            <ReviewProductForm
                                onSubmit={async (e) => {
                                    if (onSubmit) {
                                        const isSuccess = await onSubmit(e);
                                        if (isSuccess) setIsOpen(false);
                                    }
                                }}
                                score={score}
                                comment={comment}
                                isLoading={isLoading}
                                dictionary={dictionary}
                            />
                        </Dialog.Panel>
                    </Transition.Child>
                </Dialog>
            </Transition>
        </>
    );
}
